import { Link } from "@tanstack/react-router";
import { useState } from "react";
import {
  detectLongWeekends,
  formatLongWeekendRange,
  longWeekendStartSlug,
} from "@/lib/pseo";
import { parseISO, calendarYearPath, MONTH_SLUGS_ID } from "@/lib/calendar/dates";
import type { Holiday } from "@/data/holidays";
import { useLocale } from "@/i18n/LocaleContext";
import { translateHolidayName } from "@/i18n/holidays";
import { cn } from "@/lib/utils";

type LwFilter = "all" | "lw" | "harp";

function LongWeekendFilter({
  value,
  onChange,
  counts,
}: {
  value: LwFilter;
  onChange: (v: LwFilter) => void;
  counts: { all: number; lw: number; harp: number };
}) {
  const { locale } = useLocale();
  const labels: Record<LwFilter, string> =
    locale === "en"
      ? { all: "All", lw: "Long weekend", harp: "Harpitnas" }
      : { all: "Semua", lw: "Long weekend", harp: "Harpitnas" };
  const opts: LwFilter[] = ["all", "lw", "harp"];
  return (
    <div className="inline-flex rounded-md border border-border bg-background p-0.5 text-xs">
      {opts.map((opt) => (
        <button
          key={opt}
          type="button"
          onClick={() => onChange(opt)}
          className={cn(
            "rounded px-2.5 py-1 font-medium transition-colors",
            value === opt
              ? "bg-primary text-primary-foreground"
              : "text-muted-foreground hover:bg-accent",
          )}
        >
          {labels[opt]}{" "}
          <span className="ml-0.5 opacity-70">({counts[opt]})</span>
        </button>
      ))}
    </div>
  );
}

export function LongWeekendTable({
  year,
  holidays,
  filterRange,
  heading,
}: {
  year: number;
  holidays: Holiday[];
  /** Optional ISO range filter (inclusive). Only items overlapping this range are shown. */
  filterRange?: { startISO: string; endISO: string };
  /** Override section heading. */
  heading?: string;
}) {
  const { t, href, locale } = useLocale();
  const detected = detectLongWeekends(year, holidays);
  const all = filterRange
    ? detected.filter((lw) => !(lw.endISO < filterRange.startISO || lw.startISO > filterRange.endISO))
    : detected;
  const [filter, setFilter] = useState<LwFilter>("all");
  if (all.length === 0) return null;
  const counts = {
    all: all.length,
    lw: all.filter((x) => x.category === "long-weekend").length,
    harp: all.filter((x) => x.category === "harpitnas").length,
  };
  const list = all.filter((lw) =>
    filter === "all" ? true : filter === "lw" ? lw.category === "long-weekend" : lw.category === "harpitnas",
  );
  const harpLabel = locale === "en" ? "Harpitnas" : "Harpitnas";
  const lwLabel = locale === "en" ? "Long weekend" : "Long weekend";
  return (
    <section id="long-weekend" className="mb-8 rounded-lg border border-border bg-card p-4">
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-lg font-semibold">{heading ?? `${t.longWeekend.heading(year)} & Harpitnas`}</h2>
        <LongWeekendFilter value={filter} onChange={setFilter} counts={counts} />
      </div>
      <p className="mb-3 text-sm text-muted-foreground">{t.longWeekend.desc}</p>
      <div className="overflow-hidden rounded-md border border-border">
        <table className="w-full text-sm">
          <thead className="bg-muted/50 text-left">
            <tr>
              <th className="px-3 py-2">{locale === "en" ? "Type" : "Tipe"}</th>
              <th className="px-3 py-2">{t.longWeekend.range}</th>
              <th className="px-3 py-2">{t.longWeekend.total}</th>
              <th className="px-3 py-2">{t.longWeekend.triggers}</th>
            </tr>
          </thead>
          <tbody>
            {list.length === 0 && (
              <tr>
                <td colSpan={4} className="px-3 py-4 text-center text-muted-foreground">
                  {locale === "en" ? "No items for this filter." : "Tidak ada untuk filter ini."}
                </td>
              </tr>
            )}
            {list.map((lw) => (
              <tr key={lw.startISO} className="border-t border-border">
                <td className="px-3 py-2">
                  <span
                    className={cn(
                      "rounded-md px-2 py-0.5 text-xs font-medium",
                      lw.category === "harpitnas"
                        ? "bg-cuti-bersama/10 text-cuti-bersama"
                        : "bg-red-date/10 text-red-date",
                    )}
                  >
                    {lw.category === "harpitnas" ? harpLabel : lwLabel}
                  </span>
                </td>
                <td className="px-3 py-2">
                  {(() => {
                     const a = parseISO(lw.startISO)!;
                     const harpHref = `/harpitnas/${a.y}/${MONTH_SLUGS_ID[a.m - 1]}`;
                    const dateHref = `/${longWeekendStartSlug(lw)}`;
                    return (
                      <Link
                        to={href(lw.category === "harpitnas" ? harpHref : dateHref) as never}
                        className="text-primary hover:underline"
                      >
                        {formatLongWeekendRange(lw, locale)}
                      </Link>
                    );
                  })()}
                </td>
                <td className="px-3 py-2 font-medium">{t.common.daysCount(lw.totalDays)}</td>
                <td className="px-3 py-2 text-muted-foreground">
                  {lw.triggers.map((tr) => translateHolidayName(tr.name, locale)).join(", ") || t.longWeekend.fallback}
                  {lw.harpitISO && (
                    <div className="mt-0.5 text-xs">
                      <span className="font-medium text-cuti-bersama">
                        {locale === "en" ? "Take leave: " : "Cuti: "}
                      </span>
                      {lw.harpitISO.slice(8, 10)}/{lw.harpitISO.slice(5, 7)}
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

// FAQPage JSON-LD removed (Google deprecated FAQ rich results, May 2026).
// Component is kept as a no-op for backward-compatibility with callers.
export function FaqJsonLd(_props: { items: { q: string; a: string }[] }) {
  return null;
}

export function CrossLinks({ year, current }: { year: number; current: "libur" | "cuti" | "tanggal" }) {
  const { t, href } = useLocale();
  return (
    <div className="mt-6 rounded-lg border border-border bg-muted/40 p-4">
      <h2 className="mb-2 text-sm font-semibold">{t.common.relatedPages} {year}</h2>
      <div className="flex flex-wrap gap-2 text-sm">
        {current !== "libur" && (
          <Link to={href(`/libur-nasional/${year}`) as never} className="rounded-md bg-card px-3 py-1.5 hover:bg-accent">
            {t.common.nationalHolidays} {year}
          </Link>
        )}
        {current !== "cuti" && (
          <Link to={href(`/cuti-bersama/${year}`) as never} className="rounded-md bg-card px-3 py-1.5 hover:bg-accent">
            {t.common.jointLeave} {year}
          </Link>
        )}
        {current !== "tanggal" && (
          <Link to={href(`/tanggal-merah/${year}`) as never} className="rounded-md bg-card px-3 py-1.5 hover:bg-accent">
            {t.common.redDates} {year}
          </Link>
        )}
        <Link to={href(calendarYearPath(year)) as never} className="rounded-md bg-card px-3 py-1.5 hover:bg-accent">
          {t.common.calendar} {year}
        </Link>
      </div>
    </div>
  );
}